import { useMutation, useQuery, useQueryClient, UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { enum_api_uri } from "../utils/enum";
import axiosInstance from "./axiosInstance";

// 모닝택배 일자별 입고지 기사 관리 (입고지 + 기사 + 날짜 매핑)
export interface DriverSchedule {
    schedule_no: number; // 대표(묶음의 최초일) schedule_no
    schedule_nos: number[]; // 묶음에 포함된 모든 schedule_no
    warehouse_no: number;
    warehouse_name: string;
    port_name: string;
    work_date: string; // YYYY-MM-DD (대표일)
    work_start_date: string; // 묶음 시작일
    work_end_date: string; // 묶음 종료일
    day_count: number; // 묶음 일수
    driver_no: number;
    driver_name: string;
    phone: string;
    car_no: string;
}

export interface DriverScheduleListResponse {
    limit: number;
    current_page: number;
    last_page: number;
    total_count: number;
    data_list: DriverSchedule[];
}

interface DriverScheduleListRequest {
    // 통합 검색 — 항구명/입고지명/기사명 전체 대상
    searchtxt?: string;
    // 날짜 기간 검색 (시작일~종료일)
    work_start_date?: string;
    work_end_date?: string;
    page?: number;
    limit?: number | string;
}

// 등록 전용 — 기간(시작일~종료일)으로 일별 다건 생성
interface DriverScheduleCreateBody {
    warehouse_no: number | string;
    driver_no: number | string;
    work_start_date: string;
    work_end_date: string;
}

// 수정 전용 — 기존 묶음(schedule_nos)을 새 기간으로 교체
interface DriverScheduleUpdateBody {
    warehouse_no: number | string;
    driver_no: number | string;
    work_start_date: string;
    work_end_date: string;
    schedule_nos: number[];
}

export const useGetConsoleDriverSchedules = (
    params: DriverScheduleListRequest,
): UseQueryResult<DriverScheduleListResponse, Error> => {
    return useQuery({
        queryKey: [
            "useGetConsoleDriverSchedules",
            params.searchtxt,
            params.work_start_date,
            params.work_end_date,
            params.page,
            params.limit,
        ],
        queryFn: async () => {
            const response = await axiosInstance.get(enum_api_uri.get_console_driver_schedules, { params });
            return response.data.data;
        },
    });
};

export const useGetConsoleDriverScheduleDetail = (schedule_no: string): UseQueryResult<DriverSchedule, Error> => {
    const url = enum_api_uri.get_console_driver_schedule_detail.replace(":schedule_no", schedule_no);
    return useQuery({
        queryKey: ["useGetConsoleDriverScheduleDetail", schedule_no],
        queryFn: async () => {
            const response = await axiosInstance.get(url);
            return response.data.data;
        },
        enabled: !!schedule_no,
    });
};

export const usePostConsoleDriverSchedule = (): UseMutationResult<any, Error, DriverScheduleCreateBody> => {
    return useMutation({
        mutationFn: async (body: DriverScheduleCreateBody) => {
            try {
                const response = await axiosInstance.post(enum_api_uri.post_console_driver_schedule, body);
                return response.data;
            } catch (error: any) {
                // 백엔드 안내 메시지(예: 중복 날짜)를 그대로 노출
                throw new Error(error?.response?.data?.message || error?.message || "등록에 실패했습니다.");
            }
        },
    });
};

export const usePutConsoleDriverSchedule = (): UseMutationResult<
    any,
    Error,
    DriverScheduleUpdateBody & { schedule_no: string }
> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ schedule_no, ...body }: DriverScheduleUpdateBody & { schedule_no: string }) => {
            const url = enum_api_uri.put_console_driver_schedule.replace(":schedule_no", schedule_no);
            try {
                const response = await axiosInstance.put(url, body);
                return response.data;
            } catch (error: any) {
                // 백엔드 안내 메시지(예: 중복 날짜)를 그대로 노출
                throw new Error(error?.response?.data?.message || error?.message || "수정에 실패했습니다.");
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["useGetConsoleDriverScheduleDetail"] });
            queryClient.invalidateQueries({ queryKey: ["useGetConsoleDriverSchedules"] });
        },
    });
};

export const useDeleteConsoleDriverSchedule = (
    onSuccessCallback?: () => void,
): UseMutationResult<any, Error, { schedule_no: number[] }> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (body: { schedule_no: number[] }) => {
            const response = await axiosInstance.delete(enum_api_uri.delete_console_driver_schedule, { data: body });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["useGetConsoleDriverSchedules"] });
            onSuccessCallback?.();
        },
    });
};
